import { useEffect, useRef, useState, useCallback } from "react";
import { useAtomValue } from "@effect-atom/atom-react";
import type * as Monaco from "monaco-editor";
import { getMonaco, setMonacoTheme, formatXml, validateAndSetMarkers } from "../lib/monaco";
import { darkModeAtom } from "../atoms";

interface MonacoEditorProps {
  value: string;
  onChange?: (value: string) => void;
  language: "xml" | "xsl";
  readOnly?: boolean;
}

const VALIDATION_DELAY = 300; // ms

export function MonacoEditor({
  value,
  onChange,
  language,
  readOnly = false,
}: MonacoEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const validationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const darkMode = useAtomValue(darkModeAtom);

  // Debounced validation
  const scheduleValidation = useCallback(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      if (editorRef.current && monacoRef.current && !readOnly) {
        const model = editorRef.current.getModel();
        if (model) {
          validateAndSetMarkers(monacoRef.current, model, "xml-validator");
        }
      }
    }, VALIDATION_DELAY);
  }, [readOnly]);

  // Initialize editor
  useEffect(() => {
    let mounted = true;

    const initEditor = async () => {
      if (!containerRef.current) return;

      const monaco = await getMonaco();
      if (!mounted || !containerRef.current) return;

      monacoRef.current = monaco;

      const editor = monaco.editor.create(containerRef.current, {
        value,
        language: "xml",
        theme: darkMode ? "xsltransform-dark" : "xsltransform-light",
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 14,
        lineNumbers: "on",
        wordWrap: "on",
        readOnly,
        tabSize: 2,
        quickSuggestions: {
          other: true,
          comments: false,
          strings: true,
        },
        suggestOnTriggerCharacters: true,
      });

      editorRef.current = editor;
      setIsLoading(false);

      // Register format XML action (appears in command palette with Ctrl+Shift+P / Cmd+Shift+P)
      editor.addAction({
        id: "format-xml",
        label: "Format XML",
        keybindings: [
          monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
        ],
        contextMenuGroupId: "1_modification",
        contextMenuOrder: 1.5,
        run: (ed) => {
          const currentValue = ed.getValue();
          try {
            const formatted = formatXml(currentValue);
            const fullRange = ed.getModel()?.getFullModelRange();
            if (fullRange) {
              ed.executeEdits("format-xml", [
                { range: fullRange, text: formatted },
              ]);
            }
          } catch (e) {
            console.error("Failed to format XML:", e);
          }
        },
      });

      // Initial validation
      if (!readOnly) {
        const model = editor.getModel();
        if (model) {
          validateAndSetMarkers(monaco, model, "xml-validator");
        }
      }

      if (onChange) {
        editor.onDidChangeModelContent(() => {
          onChange(editor.getValue());
          scheduleValidation();
        });
      }
    };

    initEditor();

    return () => {
      mounted = false;
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
      if (editorRef.current) {
        editorRef.current.dispose();
        editorRef.current = null;
      }
    };
  }, []);

  // Update theme when dark mode changes
  useEffect(() => {
    setMonacoTheme(darkMode);
  }, [darkMode]);

  // Update value when prop changes (controlled)
  useEffect(() => {
    if (editorRef.current) {
      const currentValue = editorRef.current.getValue();
      if (value !== currentValue) {
        const selection = editorRef.current.getSelection();
        editorRef.current.setValue(value);
        if (selection) {
          editorRef.current.setSelection(selection);
        }
      }
    }
  }, [value]);

  // Update readOnly when prop changes
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.updateOptions({ readOnly });
    }
  }, [readOnly]);

  return (
    <div ref={containerRef} style={{ height: "100%", width: "100%" }}>
      {isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "#666",
          }}
        >
          Loading editor...
        </div>
      )}
    </div>
  );
}
